import React from 'react'
import { Button, ButtonProps } from '@mui/material'
import GatsbyLink from 'gatsby-link'
import { UserEventType } from '@flock/utils'

import slugify from 'slugify'
import { TrackingConfig, useTracking } from '../useTracking'
import { getNodeText } from '../../utils'

type TrackedButtonProps = ButtonProps & {
  trackingConfig?: TrackingConfig
  to?: string
  target?: string
  rel?: string
}

const TrackedButton = (props: TrackedButtonProps) => {
  const {
    onClick,
    trackingConfig,
    to,
    target,
    rel,
    variant,
    color,
    size,
    disabled,
    sx,
    children,
    ...otherProps
  } = props

  const { track } = useTracking()
  const onClickTracked = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    let trackingName = 'button-click'
    if (trackingConfig?.name) {
      trackingName = trackingConfig?.name
    } else if (props.children) {
      const nodeText = getNodeText(props.children)
      if (nodeText) {
        trackingName = slugify(nodeText.toLowerCase())
      }
    }
    track(trackingName, {
      type: 'button',
      ...trackingConfig?.properties,
      actionType: UserEventType.BUTTON_CLICK,
    })
    if (onClick) {
      onClick(event as React.MouseEvent<HTMLButtonElement, MouseEvent>)
    }
  }

  if (to) {
    // Use anchor tag for external links (target="_blank")
    // gatsby link doesn't support target="_blank" https://github.com/gatsbyjs/gatsby/issues/13825
    if (target === '_blank') {
      return (
        <Button
          component="a"
          href={to}
          variant={variant}
          color={color}
          size={size}
          disabled={disabled}
          onClick={onClickTracked}
          sx={{
            ...sx,
            textAlign: 'center',
          }}
          target={target}
          rel={rel}
        >
          {children}
        </Button>
      )
    }
    // Use GatsbyLink for internal navigation
    return (
      <Button
        component={GatsbyLink}
        variant={variant}
        color={color}
        size={size}
        disabled={disabled}
        onClick={onClickTracked}
        sx={{
          ...sx,
          textAlign: 'center',
        }}
        to={to}
      >
        {children}
      </Button>
    )
  }

  return (
    <Button
      variant={variant}
      color={color}
      size={size}
      disabled={disabled}
      onClick={onClickTracked}
      sx={sx}
      {...otherProps}
    >
      {children}
    </Button>
  )
}

TrackedButton.defaultProps = {
  trackingConfig: null,
  to: undefined,
  target: undefined,
  rel: undefined,
}

export default TrackedButton
